import { supabase, hasRealCredentials } from './supabase'
import { log } from '../utils/logger'
import { errorHandler } from '../utils/errorHandler'
import { withNetworkResilience } from '../utils/networkResilience'

// ================================================================
// TYPES & INTERFACES (Based on Database Schema)
// ================================================================

export interface BlogPost {
  id: string
  date: string
  publish_date: string
  title: string
  content: string
  excerpt: string | null
  header_image: string | null
  audio_url: string | null
  published: boolean
  created_at: string
  updated_at: string | null
}

export interface Book {
  id: string
  title: string
  author: string
  cover_image: string | null
  status: 'want_to_read' | 'currently_reading' | 'completed' | 'did_not_finish'
  review: string | null
  overall_rating: number | null
  readability_rating: number | null
  length_rating: number | null
  richness_rating: number | null
  genre: string | null
  pages: number | null
  current_page: number
  progress_percentage: number
  start_date: string | null
  end_date: string | null
  notes: string | null
  amazon_url: string | null
  created_at: string
  updated_at: string | null
}

export interface Project {
  id: string
  title: string
  status: 'completed' | 'in_progress' | 'planned'
  live_url: string | null
  time_consumption_rating: number | null
  complexity_rating: number | null
  satisfaction_rating: number | null
  learning_value_rating: number | null
  created_at: string
  updated_at: string | null
}

export interface LegacyContent {
  id: string
  title: string
  content: string
  category: string | null
  date_created: string | null
  is_published: boolean
}

// ================================================================
// DATABASE API CLASS
// ================================================================

export class PublicAPI {
  // ================================================================
  // BLOG POSTS
  // ================================================================

  async getPublishedPosts(): Promise<BlogPost[]> {
    try {
      if (!hasRealCredentials) {
        log.warn('No Supabase credentials configured - no posts available', 'PublicAPI')
        return []
      }

      log.info('Fetching published blog posts from database', 'PublicAPI')
      
      const { data, error } = await supabase.rpc('public_get_published_posts')
      
      if (error) {
        log.error('Error fetching blog posts from database', 'PublicAPI', { error: error.message })
        throw errorHandler.normalizeError(error, 'getPublishedPosts')
      }

      if (!data || data.length === 0) {
        log.info('No published blog posts found', 'PublicAPI')
        return []
      }

      log.info(`Successfully fetched ${data.length} blog posts`, 'PublicAPI')
      return data as BlogPost[]
      
    } catch (err) {
      log.error('Failed to fetch blog posts', 'PublicAPI', { error: err })
      return []
    }
  }

  async getPostByDate(postDate: string): Promise<BlogPost[]> {
    try {
      if (!hasRealCredentials) {
        log.warn('No Supabase credentials configured - no posts available', 'PublicAPI')
        return []
      }

      log.info('Fetching blog post by date from database', 'PublicAPI', { date: postDate })
      
      const { data, error } = await supabase.rpc('public_get_post_by_date', {
        post_date: postDate
      })
      
      if (error) {
        log.error('Error fetching blog post by date', 'PublicAPI', { error: error.message, date: postDate })
        throw errorHandler.normalizeError(error, 'getPostByDate')
      }

      return (data as BlogPost[]) || []
      
    } catch (err) {
      log.error('Failed to fetch blog post by date', 'PublicAPI', { error: err, date: postDate })
      return []
    }
  }

  // ================================================================
  // BOOKS
  // ================================================================

  async getPublishedBooks(): Promise<Book[]> {
    try {
      if (!hasRealCredentials) {
        log.warn('No Supabase credentials configured - no books available', 'PublicAPI')
        return []
      }

      return await withNetworkResilience(
        async () => {
          log.info('Fetching books from database', 'PublicAPI')
          
          const { data, error } = await supabase.rpc('public_get_books')
          
          if (error) {
            log.error('Error fetching books from database', 'PublicAPI', { error: error.message })
            throw errorHandler.normalizeError(error, 'getPublishedBooks')
          }

          if (!data || data.length === 0) {
            log.info('No books found', 'PublicAPI')
            return []
          }

          log.info(`Successfully fetched ${data.length} books`, 'PublicAPI')
          return data as Book[]
        },
        'getPublishedBooks',
        {
          circuitName: 'supabase-books',
          rateLimitKey: 'books-api'
        }
      )
      
    } catch (err) {
      log.error('Failed to fetch books after all resilience attempts', 'PublicAPI', { error: err })
      return []
    }
  }

  async getCurrentBook(): Promise<Book | null> {
    try {
      if (!hasRealCredentials) {
        log.warn('No Supabase credentials configured - no current book available', 'PublicAPI')
        return null
      }

      return await withNetworkResilience(
        async () => {
          log.info('Fetching current book from database', 'PublicAPI')
          
          const { data, error } = await supabase.rpc('public_get_current_book')
          
          if (error) {
            log.error('Error fetching current book from database', 'PublicAPI', { error: error.message })
            throw errorHandler.normalizeError(error, 'getCurrentBook')
          }

          if (!data || data.length === 0) {
            log.info('No current book found', 'PublicAPI')
            return null
          }

          log.info('Successfully fetched current book', 'PublicAPI')
          return data[0] as Book
        },
        'getCurrentBook',
        {
          circuitName: 'supabase-current-book',
          rateLimitKey: 'current-book-api'
        }
      )
      
    } catch (err) {
      log.error('Failed to fetch current book after all resilience attempts', 'PublicAPI', { error: err })
      return null
    }
  }

  // ================================================================
  // PROJECTS
  // ================================================================

  async getPublishedProjects(): Promise<Project[]> {
    try {
      if (!hasRealCredentials) {
        log.warn('No Supabase credentials configured - no projects available', 'PublicAPI')
        return []
      }

      log.info('Fetching projects from database', 'PublicAPI')

      // Query api.projects directly (requires api schema to be exposed in Supabase Settings,
      // or deploy the public_get_projects() RPC from scripts/setup-projects-function.sql).
      const { data, error } = await supabase
        .schema('api')
        .from('projects')
        .select('id, title, status, live_url, time_consumption_rating, complexity_rating, satisfaction_rating, learning_value_rating, created_at, updated_at')
        .order('created_at', { ascending: false })

      if (error) {
        log.error('Error fetching projects from database', 'PublicAPI', { error: error.message })
        throw errorHandler.normalizeError(error, 'getPublishedProjects')
      }

      if (!data || data.length === 0) {
        log.info('No projects found', 'PublicAPI')
        return []
      }

      log.info(`Successfully fetched ${data.length} projects`, 'PublicAPI')
      return data as Project[]
      
    } catch (err) {
      log.error('Failed to fetch projects', 'PublicAPI', { error: err })
      return []
    }
  }

  // ================================================================
  // LEGACY CONTENT
  // ================================================================

  async getLegacyContent(): Promise<LegacyContent[]> {
    try {
      if (!hasRealCredentials) {
        log.warn('Using empty legacy content - no Supabase credentials configured', 'PublicAPI')
        return []
      }

      log.info('Fetching legacy content from database', 'PublicAPI')
      
      const { data, error } = await supabase.rpc('public_get_legacy_items')
      
      if (error) {
        log.error('Error fetching legacy content from database', 'PublicAPI', { error: error.message })
        throw errorHandler.normalizeError(error, 'getLegacyContent')
      }

      if (!data || data.length === 0) {
        log.info('No legacy content found', 'PublicAPI')
        return []
      }

      log.info(`Successfully fetched ${data.length} legacy items`, 'PublicAPI')
      return data as LegacyContent[]
      
    } catch (err) {
      log.error('Failed to fetch legacy content', 'PublicAPI', { error: err })
      return []
    }
  }
}

// ================================================================
// EXPORTED INSTANCE
// ================================================================

export const publicAPI = new PublicAPI()

// ================================================================
// UTILITY FUNCTIONS
// ================================================================

export const getSupabaseStatus = () => {
  return {
    hasCredentials: hasRealCredentials,
    url: import.meta.env.VITE_SUPABASE_URL || 'Not configured',
    keyConfigured: !!(import.meta.env.VITE_SUPABASE_ANON_KEY)
  }
}

export const testConnection = async () => {
  try {
    if (!hasRealCredentials) {
      return { success: false, message: 'No Supabase credentials configured' }
    }

    // Test with a simple query
    const { data, error } = await supabase.rpc('public_get_published_posts')
    
    if (error) {
      return { success: false, message: error.message }
    }

    return { success: true, message: 'Connection successful', recordCount: data?.length || 0 }
  } catch (err) {
    return { success: false, message: `Connection failed: ${err}` }
  }
}
